import { getLocaleSearchTerms, normalizeAppLocale } from './locales';

const SUBJECT_SEARCH_PROFILES = {
  'Accounting': {
    aliases: ['Accounting'],
    intents: ['worked examples', 'journal entries', 'revision', 'exam questions'],
  },
  'Accounting (EMS component)': {
    aliases: ['Accounting'],
    intents: ['worked examples', 'journal entries', 'revision', 'exam questions'],
  },
  'Afrikaans FAL': {
    aliases: ['Afrikaans FAL', 'Afrikaans'],
    intents: ['grammar', 'essay', 'summary', 'literature'],
  },
  'Business Studies': {
    aliases: ['Business Studies', 'Business'],
    intents: ['revision', 'summary', 'exam questions', 'case study'],
  },
  'Business (EMS component)': {
    aliases: ['Business Studies', 'Business'],
    intents: ['revision', 'summary', 'exam questions', 'case study'],
  },
  'Computer Applications Technology (CAT)': {
    aliases: ['CAT', 'Computer Applications Technology'],
    intents: ['tutorial', 'practical', 'step by step', 'revision'],
  },
  'Creative Arts': {
    aliases: ['Creative Arts', 'Art'],
    intents: ['explained', 'practical', 'revision', 'summary'],
  },
  'EMS (Economic & Management Sciences)': {
    aliases: ['EMS', 'Economic Management Sciences'],
    intents: ['revision', 'summary', 'worked examples', 'exam questions'],
  },
  'English HL': {
    aliases: ['English HL', 'English'],
    intents: ['summary', 'essay', 'literature', 'grammar'],
  },
  'Geography': {
    aliases: ['Geography'],
    intents: ['mapwork', 'revision', 'summary', 'case study'],
  },
  'History': {
    aliases: ['History'],
    intents: ['summary', 'essay', 'source-based questions', 'revision'],
  },
  'Information Technology': {
    aliases: ['Information Technology', 'IT'],
    intents: ['tutorial', 'practical', 'step by step', 'revision'],
  },
  'Life Orientation': {
    aliases: ['Life Orientation'],
    intents: ['summary', 'revision', 'explained', 'study guide'],
  },
  'Life Sciences': {
    aliases: ['Life Sciences', 'Biology'],
    intents: ['explained', 'summary', 'revision', 'exam questions'],
  },
  'Life Skills': {
    aliases: ['Life Skills'],
    intents: ['activities', 'explained', 'revision', 'summary'],
  },
  'Mathematics': {
    aliases: ['Mathematics', 'Maths'],
    intents: ['worked examples', 'revision', 'exam questions', 'tutorial'],
  },
  'Natural Sciences': {
    aliases: ['Natural Sciences', 'Science'],
    intents: ['explained', 'worked examples', 'revision', 'exam questions'],
  },
  'Natural Sciences & Technology (NST)': {
    aliases: ['NST', 'Natural Sciences and Technology'],
    intents: ['explained', 'practical', 'revision', 'worked examples'],
  },
  'Physics (Physical Sciences)': {
    aliases: ['Physical Sciences', 'Physics'],
    intents: ['worked examples', 'problems', 'exam questions', 'experiment'],
  },
  'Technology': {
    aliases: ['Technology'],
    intents: ['practical', 'step by step', 'revision', 'explained'],
  },
};

const TOPIC_SPECIFIC_INTENTS = [
  { pattern: /\b(statistics|probability|algebra|functions?|equations?|trigonometry|calculus|finance)\b/i, intents: ['worked examples', 'exam questions', 'revision'] },
  { pattern: /\b(mapwork|geomorphology|climate|settlement|resources?)\b/i, intents: ['mapwork', 'case study', 'revision'] },
  { pattern: /\b(essay|source[- ]based|source based)\b/i, intents: ['essay', 'source-based questions', 'summary'] },
  { pattern: /\b(grammar|comprehension|poetry|novel|drama|language)\b/i, intents: ['grammar', 'summary', 'essay'] },
  { pattern: /\b(journal|ledger|cash flow|inventory|budget|depreciation)\b/i, intents: ['journal entries', 'worked examples', 'revision'] },
  { pattern: /\b(html|excel|word|access|database|sql|coding|programming|scratch)\b/i, intents: ['tutorial', 'practical', 'step by step'] },
];

const STOPWORDS = new Set([
  'a',
  'an',
  'and',
  'for',
  'from',
  'grade',
  'in',
  'of',
  'on',
  'south',
  'africa',
  'the',
  'to',
]);

function normalizeWhitespace(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function uniqueValues(values) {
  const seen = new Set();
  return values.filter((value) => {
    const normalizedValue = normalizeWhitespace(value).toLowerCase();
    if (!normalizedValue || seen.has(normalizedValue)) {
      return false;
    }
    seen.add(normalizedValue);
    return true;
  });
}

function compactTokenKey(value) {
  return normalizeWhitespace(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function buildQuery(parts) {
  const seen = new Set();
  const compacted = [];

  parts.forEach((part) => {
    const normalizedPart = normalizeWhitespace(part);
    const partKey = compactTokenKey(normalizedPart);
    if (!normalizedPart || !partKey || seen.has(partKey)) {
      return;
    }
    seen.add(partKey);
    compacted.push(normalizedPart);
  });

  return compacted.join(' ');
}

function tokenizeSearchText(value) {
  return uniqueValues(
    normalizeTopicForSearch(value)
      .toLowerCase()
      .split(' ')
      .map((token) => token.trim())
      .filter((token) => token.length > 2 && !STOPWORDS.has(token))
  );
}

export function splitCurriculumHeadings(headings) {
  const text = normalizeWhitespace(headings);
  const parts = [];
  let current = '';
  let depth = 0;

  for (const char of text) {
    if (char === '(') {
      depth += 1;
    } else if (char === ')' && depth > 0) {
      depth -= 1;
    }

    if (char === ';' && depth === 0) {
      const nextPart = normalizeWhitespace(current);
      if (nextPart) {
        parts.push(nextPart);
      }
      current = '';
      continue;
    }

    current += char;
  }

  const finalPart = normalizeWhitespace(current);
  if (finalPart) {
    parts.push(finalPart);
  }

  return parts;
}

export function normalizeTopicForSearch(topic) {
  return normalizeWhitespace(topic)
    .replace(/[()]/g, ' ')
    .replace(/[;:]/g, ' ')
    .replace(/\//g, ' ')
    .replace(/\s*&\s*/g, ' and ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function getSubjectSearchProfile(subject) {
  const profile = SUBJECT_SEARCH_PROFILES[subject];
  if (profile) {
    return {
      aliases: uniqueValues(profile.aliases),
      intents: uniqueValues(profile.intents),
    };
  }

  const fallback = normalizeWhitespace(subject);
  return {
    aliases: [fallback],
    intents: ['explained', 'revision', 'summary', 'tutorial'],
  };
}

export function getSubjectQueryTerms(subject) {
  return getSubjectSearchProfile(subject).aliases.map((alias) => alias.toLowerCase());
}

function getSubjectIntentTerms(subject, topic) {
  const profile = getSubjectSearchProfile(subject);
  const topicText = normalizeTopicForSearch(topic);
  const topicSpecific = TOPIC_SPECIFIC_INTENTS.flatMap((rule) =>
    rule.pattern.test(topicText) ? rule.intents : []
  );

  return uniqueValues([...topicSpecific, ...profile.intents]).slice(0, 4);
}

function getOptionalKeywordHint(searchKeywords, topic) {
  const topicTokens = new Set(tokenizeSearchText(topic));
  return uniqueValues(
    (Array.isArray(searchKeywords) ? searchKeywords : [])
      .map((keyword) => normalizeTopicForSearch(keyword))
      .filter((keyword) => {
        const keywordTokens = tokenizeSearchText(keyword);
        return keywordTokens.some((token) => !topicTokens.has(token));
      })
  )[0] || '';
}

export function buildCurriculumSearchQueries({
  grade,
  subject,
  topic,
  locale = 'en',
  localizedSubject = '',
  localizedTopic = '',
  searchKeywords = [],
}) {
  const localeKey = normalizeAppLocale(locale);
  const topicText = normalizeTopicForSearch(localizedTopic || topic);
  const subjectProfile = getSubjectSearchProfile(subject);
  const formalSubject = normalizeWhitespace(localizedSubject || subjectProfile.aliases[0] || subject);
  const studentSubject = normalizeWhitespace(subjectProfile.aliases[1] || subjectProfile.aliases[0] || subject);
  const shortSubject = normalizeWhitespace(subjectProfile.aliases[2] || studentSubject);
  const intentTerms = getSubjectIntentTerms(subject, topicText);
  const localeHint = localeKey === 'en' ? '' : getLocaleSearchTerms(localeKey)[0];
  const keywordHint = getOptionalKeywordHint(searchKeywords, topicText);

  const variants = [
    buildQuery([`Grade ${grade}`, formalSubject, topicText]),
    buildQuery([topicText, `Grade ${grade}`, shortSubject]),
    buildQuery([`Grade ${grade}`, studentSubject, topicText, intentTerms[0]]),
    buildQuery([topicText, `Grade ${grade}`, studentSubject, intentTerms[1]]),
    buildQuery([`Grade ${grade}`, formalSubject, topicText, 'explained']),
    buildQuery([topicText, `Grade ${grade}`, studentSubject, intentTerms[2]]),
    localeHint ? buildQuery([topicText, `Grade ${grade}`, shortSubject, localeHint]) : '',
    keywordHint ? buildQuery([`Grade ${grade}`, studentSubject, topicText, keywordHint]) : '',
  ];

  return uniqueValues(variants).slice(0, 6);
}

export function buildCurriculumSearchText(args) {
  return buildCurriculumSearchQueries(args)[0] || '';
}

export function buildCurriculumSearchPayload({
  grade,
  subject,
  topic,
  phase = '',
  notes = '',
  locale = 'en',
  localizedSubject = '',
  localizedTopic = '',
  searchKeywords = [],
}) {
  return {
    grade: String(grade).trim(),
    subject: normalizeWhitespace(subject),
    topic: normalizeTopicForSearch(topic),
    phase: normalizeWhitespace(phase),
    notes: normalizeWhitespace(notes),
    locale: normalizeAppLocale(locale),
    localizedSubject: normalizeWhitespace(localizedSubject),
    localizedTopic: normalizeWhitespace(localizedTopic),
    searchKeywords: Array.isArray(searchKeywords)
      ? searchKeywords.map((keyword) => normalizeWhitespace(keyword)).filter(Boolean)
      : [],
  };
}

export function buildCurriculumSearchUrl(args) {
  return `https://www.youtube.com/results?search_query=${encodeURIComponent(buildCurriculumSearchText(args))}`;
}
